import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "../components/ui/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Icons } from "../utils/icons";
import XanoAPI from "../lib/XanoAPI";
import { z } from "zod";
// TODO: Check if this import is actually used - commented out for cleanup
// import { verifyTOTPToken } from "../utils/otp";
import TextController from "../components/form-fields/TextController";
import logo from "../images/logo.png";
import SignInImage from "../images/signin.jpg";

// Validation schemas
const loginSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(1, { message: "Password is required" }),
});

interface AuthFormProps extends React.HTMLAttributes<HTMLDivElement> {}

export default function AuthForm({ className, ...props }: AuthFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isTOTPRequired, setIsTOTPRequired] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authToken, setAuthToken] = useState("");
  const api = XanoAPI.getInstance();

  const methods = useForm<{ twoFactorCode: string }>({
    defaultValues: { twoFactorCode: "" },
  });

  const { handleSubmit } = methods;

  const handleEmailPasswordLogin = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const credentials = { email, password };
      loginSchema.parse(credentials);

      const result = await api.login(credentials);
      setIsLoading(false);

      if (!result.totp) {
        // If TOTP is not required, log the user in directly
        await api.getCurrentUser();
        window.location.replace("/");
      } else {
        // If TOTP is required, switch to TOTP verification view
        setIsTOTPRequired(true);
        setAuthToken(result.authToken); // Save authToken for verification
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors.map((e) => e.message).join(", "),
        });
      } else {
        toast({
          title: "Login Error",
          description: "An error occurred while logging in. Please try again.",
        });
      }
      setIsLoading(false);
    }
  };

  async function handleTOTPVerification(data: { twoFactorCode: string }) {
    setIsLoading(true);

    try {
      if (!data.twoFactorCode || data.twoFactorCode.length !== 6) {
        toast({
          title: "Invalid Code",
          description: "Please enter a valid 6-digit code.",
        });
        setIsLoading(false);
        return;
      }

      const api = XanoAPI.getInstance();
      const result = await api.validateLoginTOTP(authToken, data.twoFactorCode);

      if (result.authToken) {
        toast({
          title: "Verification Successful",
          description: "The code has been verified successfully.",
        });
        if (result.authToken) {
          api.setToken(result.authToken); // Store the new auth token
        }
        window.location.replace("/"); // Redirect to the dashboard
      } else {
        toast({
          title: "Verification Failed",
          description: "The code is incorrect. Please try again.",
        });
      }
    } catch (error) {
      console.error("Verification Error:", error);
      toast({
        title: "Error",
        description: "An error occurred during verification. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="container relative hidden h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      {/* Left side image */}
      <div className="relative hidden h-full flex-col bg-muted text-white dark:border-r lg:flex">
        <div className="absolute inset-0 bg-zinc-200 bg-cover" />
        <div
          className="relative z-20 mt-auto bg-cover bg-center bg-no-repeat h-screen"
          style={{ backgroundImage: `url(${SignInImage})` }}
        />
      </div>

      {/* Form content */}
      <div className="lg:p-8">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <div className="flex items-center justify-center w-full">
              <div className="w-[80px] h-[80px] relative mb-8">
                <img
                  src={logo}
                  alt="logo"
                  sizes="(max-width: 768px) 100px, 50vw"
                />
              </div>
            </div>

            {!isTOTPRequired ? (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">
                  Sign In to Your Account
                </h1>
                <p className="text-sm text-muted-foreground">
                  Enter your email and password to continue
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">
                  Enter Your Verification Code
                </h1>
                <p className="text-sm text-muted-foreground">
                  Enter the 6-digit code from your Authenticator app below
                </p>
              </>
            )}
          </div>

          {!isTOTPRequired ? (
            // Email and Password Form
            <form onSubmit={handleEmailPasswordLogin}>
              <div className="grid gap-2">
                <div className="grid gap-1">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    name="email"
                    id="email"
                    placeholder="Your email address ..."
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={isLoading}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    name="password"
                    id="password"
                    type="password"
                    placeholder="Your password ..."
                    autoCapitalize="none"
                    autoCorrect="off"
                    disabled={isLoading}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <Button disabled={isLoading} className="sign-in">
                  {isLoading && (
                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Sign In with Email
                </Button>
              </div>
            </form>
          ) : (
            // TOTP Verification Form
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(handleTOTPVerification)}
                className="space-y-6"
              >
                <TextController
                  name="twoFactorCode"
                  label="Two-Factor Authentication Code"
                  placeholder="Enter the 6-digit code"
                  showPrefix={false}
                  validation={{
                    required: "Code is required.",
                    minLength: {
                      value: 6,
                      message: "Code must be 6 digits long.",
                    },
                    maxLength: {
                      value: 6,
                      message: "Code must be 6 digits long.",
                    },
                    pattern: {
                      value: /^[0-9]{6}$/,
                      message: "Code must contain only numbers.",
                    },
                  }}
                  infoMessage="This code is generated by your Authenticator app."
                />
                <Button disabled={isLoading} className="w-full">
                  {isLoading ? "Verifying..." : "Verify Code"}
                </Button>
              </form>
            </FormProvider>
          )}

          <p className="px-8 text-center text-sm text-muted-foreground">
            <>
              By clicking continue, you agree to our{" "}
              <Link
                to="/terms"
                className="underline underline-offset-4 hover:text-primary"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-4 hover:text-primary"
              >
                Privacy Policy
              </Link>
              .
            </>
          </p>
        </div>
      </div>
    </div>
  );
}
